// Verwaltung › Turnier-Einstellungen: Einträge, Status, Änderungen, Fehler.
// Ohne React, ohne Netz. Welche Felder es gibt und welche wirken, steht in
// lib/einstellungenWirkung.js (dort werden ausgeblendete Felder freigeschaltet).
import {
  ZEITPLAN_ZEILEN, zeileVon, zeitplanFelder, zusatzFuer, wertungZusatzFelder,
  wirksamerZeitplanWert, wirksameKampfrichter, zeitplanMinuten,
} from '../../lib/einstellungenWirkung';
import { regelFuer } from '../../lib/minuspunkte';
import { regelnFuer, optionenAus, dauerSekunden, dauerText } from '../../lib/kampfRegeln';

export const VORLAGEN = [
  { k: 'haftung_text', name: 'Haftung' },
  { k: 'versicherung_text', name: 'Versicherung' },
  { k: 'datenschutz_text', name: 'Datenschutz und Fotos' },
];
export const BASIS_ID = 'z:_basis';
export const klon = (o) => JSON.parse(JSON.stringify(o ?? null));
const gleich = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Zeitplan-Antwort des Servers → { start_time, pause_between_categories, disciplines }. */
export function zeitplanAus(data) {
  const d = data || {};
  return {
    start_time: d.start_time || '09:00',
    pause_between_categories: d.pause_between_categories != null ? Number(d.pause_between_categories) : 5,
    disciplines: d.disciplines && typeof d.disciplines === 'object' ? klon(d.disciplines) : {},
  };
}

/* ═══ Gruppen und Einträge ═══ */
export function gruppen(scope, wertungZeilen) {
  const std = scope === 'std';
  return [
    { key: 'wertung', titel: 'Wertung',
      items: std
        ? wertungZeilen.map((w) => ({ id: `w:${w.division_type}`, name: w.division_type, unter: w.wertungs_system === 'bracket' ? 'K.O.' : 'Punkte' }))
        : [{ id: 'w:link', name: 'Wertung dieses Turniers', link: 'einstellungen' }] },
    { key: 'zeitplan', titel: 'Zeitplan',
      items: [{ id: BASIS_ID, name: 'Beginn und Pausen', unter: 'Grundwerte' }]
        .concat(ZEITPLAN_ZEILEN.map((z) => ({ id: `z:${z.schluessel}`, name: z.name, unter: z.gruppe }))) },
    { key: 'ausschreibung', titel: 'Ausschreibung',
      items: std
        ? VORLAGEN.map((v) => ({ id: `v:${v.k}`, name: v.name }))
        : [{ id: 'v:link', name: 'Texte dieses Turniers', link: 'ausschreibung' }] },
  ];
}
export const alleIds = (g) => g.flatMap((x) => x.items.filter((i) => !i.link).map((i) => i.id));
export const gruppeVon = (id) => (id.startsWith('w:') ? 'wertung' : id.startsWith('v:') ? 'ausschreibung' : 'zeitplan');

export function titelVon(id) {
  if (id === BASIS_ID) return 'Beginn und Pausen';
  if (id.startsWith('z:')) return zeileVon(id.slice(2))?.name || id.slice(2);
  if (id.startsWith('v:')) return VORLAGEN.find((v) => v.k === id.slice(2))?.name || '';
  return id.slice(2);
}

/* ═══ Felder je Eintrag ═══ */
const BASIS_FELDER = [
  { feld: 'start_time', label: 'Beginn', typ: 'zeit' },
  { feld: 'pause_between_categories', label: 'Pause zwischen Kategorien', einheit: 'Min', min: 0, max: 30 },
];
export const basisSchalter = () => zusatzFuer('_basis', true);

/** Felder eines Wertungs-Eintrags (sichtbar). */
export function wertungFelder(row) {
  const f = [{ feld: 'wertungs_system', label: 'Wertungssystem', typ: 'system' }];
  if (regelFuer({ divisionType: row?.division_type }).vonHand) {
    f.push({ feld: 'auto_dq_ab_verwarnung', label: 'Automatisch disqualifizieren ab Verwarnung', min: 1, max: 20, leerErlaubt: true });
  }
  wertungZusatzFelder(row?.division_type).forEach((a) => {
    if (a.nurPunkte && row?.wertungs_system === 'bracket') return;
    f.push({ feld: a.feld, label: a.label, min: a.min, max: a.max, typ: a.typ, leerErlaubt: a.typ === 'text' });
  });
  return f;
}

/** Rohwert eines Zeitplan-Felds (undefined = nicht gespeichert). */
export const zpRoh = (zp, schluessel, feld, unter) => {
  const d = zp?.disciplines?.[schluessel];
  return unter ? d?.[unter]?.[feld] : d?.[feld];
};

/* ═══ Änderungen ═══ */
export function geaenderteFelder(id, d) {
  if (!d) return [];
  if (id === BASIS_ID) {
    const out = BASIS_FELDER.filter((f) => String(d.zp.werte[f.feld]) !== String(d.zp.basis[f.feld])).map((f) => f.label);
    basisSchalter().forEach((s) => {
      const a = d.zp.werte.disciplines?._optionen?.[s.feld]; const b = d.zp.basis.disciplines?._optionen?.[s.feld];
      if (s.typ === 'schalter' ? !!a !== !!b : String(a ?? '') !== String(b ?? '')) out.push(s.label);
    });
    return out;
  }
  if (id.startsWith('z:')) {
    const k = id.slice(2);
    const a = d.zp.werte.disciplines?.[k] || {}; const b = d.zp.basis.disciplines?.[k] || {};
    const namen = Object.fromEntries(zeitplanFelder(k).map((f) => [f.feld, f.label]));
    return [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((f) => !gleich(a[f], b[f])).map((f) => namen[f] || f);
  }
  if (id.startsWith('w:')) {
    const a = d.wertung.werte[id.slice(2)]; const b = d.wertung.basis[id.slice(2)];
    if (!a || !b) return [];
    return wertungFelder(a).filter((f) => String(a[f.feld] ?? '') !== String(b[f.feld] ?? '')).map((f) => f.label);
  }
  if (id.startsWith('v:')) {
    const k = id.slice(2);
    return d.vorlagen && (d.vorlagen.werte[k] || '') !== (d.vorlagen.basis[k] || '') ? [titelVon(id)] : [];
  }
  return [];
}

/* ═══ Fehler am Feld ═══ */
/** Dauer-Feld (Minuten mit Bruchteil gespeichert; Eingabe „1:30“, „90 s“, „1,5“). */
export function pruefeDauer(wert, f) {
  if (wert === '' || wert == null) return f.leerErlaubt ? null : 'Bitte eine Dauer eintragen';
  const sek = dauerSekunden(wert);
  if (sek == null) return 'Als 1:30, 90 s oder 1,5 eintragen';
  if (f.min != null && sek < Math.round(f.min * 60)) return `Mindestens ${dauerText(Math.round(f.min * 60))} Min`;
  if (f.max != null && sek > Math.round(f.max * 60)) return `Höchstens ${dauerText(Math.round(f.max * 60))} Min`;
  return null;
}

export function pruefeZahl(wert, f) {
  if (f.typ === 'dauer') return pruefeDauer(wert, f);
  if (wert === '' || wert == null) return f.leerErlaubt ? null : 'Bitte eine Zahl eintragen';
  const n = Number(wert);
  if (!Number.isInteger(n)) return 'Nur ganze Zahlen';
  if (f.min != null && n < f.min) return f.einheit === 'Min' ? `Mindestens ${f.min} ${f.min === 1 ? 'Minute' : 'Minuten'}` : `Mindestens ${f.min}`;
  if (f.max != null && n > f.max) return `Höchstens ${f.max}${f.einheit ? ` ${f.einheit}` : ''}`;
  return null;
}

/** { 'id|feld|unter': Meldung } für alle sichtbaren Felder mit Fehler. */
export function alleFehler(d) {
  const out = {};
  if (!d) return out;
  const zp = d.zp.werte;
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(zp.start_time || ''))) out[`${BASIS_ID}|start_time|`] = 'Uhrzeit fehlt';
  const p = pruefeZahl(zp.pause_between_categories, BASIS_FELDER[1]);
  if (p) out[`${BASIS_ID}|pause_between_categories|`] = p;
  basisSchalter().filter((s) => s.typ !== 'schalter').forEach((s) => {
    const roh = zp.disciplines?._optionen?.[s.feld];
    if (roh === undefined) return; // nicht gespeichert: es gilt der Standard
    const m = pruefeZahl(roh, s);
    if (m) out[`${BASIS_ID}|${s.feld}|`] = m;
  });
  for (const z of ZEITPLAN_ZEILEN) {
    if (z.ohneWirkung) continue;
    const felder = zeitplanFelder(z.schluessel).filter((f) => f.typ !== 'schalter');
    const unter = zusatzFuer(z.schluessel, true).find((a) => a.art === 'unterteilung');
    const pruefe = (u) => felder.forEach((f) => {
      if (u && f.feld === 'breakAfter') return;
      const roh = zpRoh(zp, z.schluessel, f.feld, u);
      if (roh === undefined) return; // nicht gespeichert: es gilt der eingebaute Wert
      const m = pruefeZahl(roh, f);
      if (m) out[`z:${z.schluessel}|${f.feld}|${u || ''}`] = m;
    });
    pruefe(null);
    if (unter) unter.unter.forEach(([u]) => pruefe(u));
  }
  if (d.wertung) {
    Object.values(d.wertung.werte).forEach((row) => {
      const id = `w:${row.division_type}`;
      if (!geaenderteFelder(id, d).length) return; // nur geprüft, was geändert wird
      wertungFelder(row).forEach((f) => {
        if (f.typ === 'system' || f.typ === 'text') return;
        const m = pruefeZahl(row[f.feld], f);
        if (m) out[`${id}|${f.feld}|`] = m;
      });
      [['punkte_min_farbgurt', 'punkte_max_farbgurt'], ['punkte_min_schwarzgurt', 'punkte_max_schwarzgurt']].forEach(([von, bis]) => {
        if (!wertungFelder(row).some((f) => f.feld === bis)) return;
        if (row[von] !== '' && row[bis] !== '' && Number(row[bis]) <= Number(row[von])) out[`${id}|${bis}|`] = '„bis“ muss größer sein als „von“';
      });
    });
  }
  return out;
}
export const fehlerVon = (fehler, id) => Object.keys(fehler).filter((k) => k.startsWith(`${id}|`));

/* ═══ Status ═══ */
export const STATUS = {
  ok: ['te-st-ok', 'gesetzt'], fehlt: ['te-st-fehlt', 'nicht gespeichert'], neu: ['te-st-neu', 'geändert'],
  fehler: ['te-st-fehler', 'Fehler'], erbt: ['te-st-erbt', 'wie Vorgabe'], zu: ['te-st-zu', 'nur Plattform'], tot: ['te-st-fehlt', 'ohne Wirkung'],
};
export const PUNKT = { ok: 'te-c-ok', fehlt: 'te-c-fehlt', neu: 'te-c-neu', fehler: 'te-c-fehler', erbt: 'te-c-erbt', zu: 'te-c-zu', tot: 'te-c-fehlt' };

export function status(id, d, ctx) {
  if (fehlerVon(ctx.fehler, id).length) return 'fehler';
  if (geaenderteFelder(id, d).length) return 'neu';
  const std = ctx.scope === 'std';
  if (id.startsWith('w:')) return ctx.plattform ? 'ok' : 'zu';
  if (id.startsWith('v:')) return d.vorlagen?.werte[id.slice(2)] ? 'ok' : 'fehlt';
  const eigen = std ? d.zp.quelle === 'standard' : d.zp.quelle === 'turnier';
  if (id === BASIS_ID) {
    if (!std && Number(d.zp.matten) === 0) return 'fehlt';
    return eigen ? 'ok' : std ? 'fehlt' : 'erbt';
  }
  const z = zeileVon(id.slice(2));
  if (z?.ohneWirkung) return 'tot';
  if (!eigen) return std ? 'fehlt' : 'erbt';
  // Zusatzfelder (Verlängerung, Erholung …) haben einen Standard; „offen“ ist
  // eine Zeile nur, wenn ein Grundfeld fehlt
  return zeitplanFelder(z.schluessel).filter((f) => !f.zusatz || f.vorn)
    .some((f) => zpRoh(d.zp.werte, z.schluessel, f.feld) === undefined) ? 'fehlt' : 'ok';
}
export function statusWort(id, s, d, ctx) {
  if (s !== 'fehlt') return STATUS[s][1];
  if (id.startsWith('v:')) return 'leer';
  if (id === BASIS_ID && ctx.scope !== 'std' && Number(d.zp.matten) === 0) return 'keine Matte';
  return ctx.scope === 'std' && d.zp.quelle !== 'standard' ? 'nie gespeichert' : 'eingebaut';
}

/** Wert, den der Zeitplan nimmt, und woher er kommt. */
export function zpWert(d, vorgabe, schluessel, feld, unter) {
  const roh = zpRoh(d.zp.werte, schluessel, feld, unter);
  if (roh !== undefined) return { w: roh, her: 'gesetzt' };
  if (unter) {
    // Kette wie lib/kampfRegeln.js: Kids · Finale → Kids → Finale → Grundzeile
    const lage = { finale: unter !== 'kids', kids: unter !== 'finale' };
    return { w: wirksamerZeitplanWert(d.zp.werte.disciplines, schluessel, feld, lage), her: 'grundzeile' };
  }
  if (feld === 'anzahlKampfrichter') {
    const eigen = d.zp.quelle === 'turnier' || d.zp.quelle === 'standard';
    return { w: wirksameKampfrichter(eigen ? d.zp.werte.disciplines : null, vorgabe?.disciplines, schluessel), her: 'eingebaut' };
  }
  return { w: wirksamerZeitplanWert(d.zp.werte.disciplines, schluessel, feld), her: 'eingebaut' };
}

// Minuten (auch mit Bruchteil) → „2“ bzw. „1:30“ (lib/kampfRegeln.js dauerText)
const zahl = (min) => dauerText(Math.round(Number(min) * 60));
const minText = (min) => `${zahl(min)} Min`;

export function kurzWert(id, d) {
  if (id.startsWith('w:')) return d.wertung?.werte[id.slice(2)]?.wertungs_system === 'bracket' ? 'K.O.' : 'Punkte';
  if (id.startsWith('v:')) { const t = d.vorlagen?.werte[id.slice(2)] || ''; return t ? `${t.length} Zeichen` : 'leer'; }
  if (id === BASIS_ID) return `${d.zp.werte.start_time} · ${d.zp.werte.pause_between_categories} Min`;
  const z = zeileVon(id.slice(2));
  if (!z || z.ohneWirkung) return '–';
  const g = (f) => wirksamerZeitplanWert(d.zp.werte.disciplines, z.schluessel, f);
  if (z.art === 'person') return `${g('durationPerPerson')} Min/Pers.`;
  if (z.art === 'begegnung') return `${g('durationPerMatch')} + ${g('breakAfter')} Min`;
  if (z.art === 'rumble' || z.art === 'kampfdauer') return `${zahl(regelnFuer(d.zp.werte.disciplines, z.schluessel, {}).kampfMin)} Min`;
  return `${g('rounds')} × ${zahl(g('roundDuration'))} Min`;
}

/**
 * Rechenhilfe: „Bei 8 Startern rechnet der Zeitplan …“ mit allen Teilen aus
 * lib/kampfRegeln.js (kategorieMinuten). zusatz: weitere Zeilen (Finale,
 * Erholung, Reserve, Kids).
 */
export function rechnungText(schluessel, disziplinen, platzDrei = false) {
  const z = zeileVon(schluessel);
  if (!z || z.ohneWirkung) return null;
  const r = zeitplanMinuten(disziplinen, schluessel, 8, false, platzDrei);
  if (r.art === 'person') return { vor: 'Bei 8 Startern rechnet der Zeitplan', zahl: minText(r.minuten), nach: `(8 × (${zahl(r.je)} + ${zahl(r.pause)}))`, zusatz: [] };
  if (r.art === 'begegnung') return { vor: 'Bei 8 Startern (4 Begegnungen) rechnet der Zeitplan', zahl: minText(r.minuten), nach: `(4 × (${zahl(r.je)} + ${zahl(r.pause)}))`, zusatz: [] };
  const zusatz = [];
  const kampfWort = z.art === 'rumble' ? 'Ein Kampf bis 3 Gürtel' : 'Ein Kampf';
  const finaleAnders = r.jeFinale !== r.je;
  const k = r.kaempfe;
  const teil = finaleAnders
    ? `${k - 1} × (${zahl(r.je)} + ${zahl(r.pause)}) + Finale (${zahl(r.jeFinale)} + ${zahl(r.pause)})`
    : `${k} × (${zahl(r.je)} + ${zahl(r.pause)})`;
  const kaempfeText = r.platzDrei ? `${k} Kämpfe mit Kampf um Platz 3` : `${k} Kämpfe`;
  if (finaleAnders) zusatz.push(`Finale (nur der Endkampf): ${minText(r.jeFinale)}`);
  if (r.erholung > 0) {
    zusatz.push(r.durchErholung
      ? `Erholung ${minText(r.erholung)}: der Finalist kämpft ${r.baumRunden}-mal, die Kategorie braucht mindestens ${minText(r.mindest)} statt ${minText(r.summe)}`
      : `Erholung ${minText(r.erholung)} je Kämpfer: passt in den Ablauf (mindestens ${minText(r.mindest)})`);
  }
  if (r.reserve > 0) zusatz.push(`Reserve für Verlängerungen: ${r.reserveProzent} % von ${k} Kämpfen = ${r.reserveKaempfe} × ${minText(r.reserveJe)} = ${minText(r.reserve)}`);
  else zusatz.push(r.reserveProzent > 0 ? 'Reserve für Verlängerungen: Verlängerung 0 Min, kein Zuschlag' : 'Reserve für Verlängerungen: aus (0 %)');
  const o = optionenAus(disziplinen);
  if (o.trennAltersgruppen) {
    const kids = zeitplanMinuten(disziplinen, schluessel, 8, true, platzDrei);
    zusatz.push(`Kids (Altersklasse bis 12), 8 Starter: ${minText(kids.minuten)}, ein Kampf ${minText(kids.je)}`);
  }
  return {
    vor: `${kampfWort} ${minText(r.je)}, bei 8 Startern (${kaempfeText})`,
    zahl: minText(r.minuten),
    nach: `(${teil}${r.durchErholung ? ', Untergrenze Erholung' : ''}${r.reserve > 0 ? ` + ${zahl(r.reserve)} Reserve` : ''})`,
    zusatz,
  };
}

/* ═══ Vergleich für Konflikt und Zurücksetzen ═══ */
/** Zeilen [Bezeichnung, a, b] für alle sichtbaren Zeitplanfelder, die sich unterscheiden. */
export function zeitplanUnterschiede(a, b) {
  const out = [];
  if (String(a.start_time) !== String(b.start_time)) out.push(['Beginn', a.start_time, b.start_time]);
  if (String(a.pause_between_categories) !== String(b.pause_between_categories)) out.push(['Pause zwischen Kategorien', `${a.pause_between_categories} Min`, `${b.pause_between_categories} Min`]);
  for (const z of ZEITPLAN_ZEILEN) {
    if (z.ohneWirkung) continue;
    for (const f of zeitplanFelder(z.schluessel)) {
      if (f.typ === 'schalter') continue;
      const wa = f.feld === 'anzahlKampfrichter' ? a.disciplines?.[z.schluessel]?.[f.feld] ?? '–' : wirksamerZeitplanWert(a.disciplines, z.schluessel, f.feld);
      const wb = f.feld === 'anzahlKampfrichter' ? b.disciplines?.[z.schluessel]?.[f.feld] ?? '–' : wirksamerZeitplanWert(b.disciplines, z.schluessel, f.feld);
      const text = (w) => (f.typ === 'dauer' && w !== '–' ? zahl(w) : w);
      if (String(wa) !== String(wb)) out.push([`${z.name}: ${f.label}`, `${text(wa)}${f.einheit ? ` ${f.einheit}` : ''}`, `${text(wb)}${f.einheit ? ` ${f.einheit}` : ''}`]);
    }
  }
  return out;
}
